import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { todolistsService } from '@/services/collections/todolists';
import { useAuth } from '@/contexts/AuthContext';
import type { CreateTodoListInput } from '@/types/todolist';

export const useMyTodoLists = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['todolists', 'owned', user?.uid],
    queryFn: () => todolistsService.getOwned(user!.uid),
    enabled: !!user,
    refetchOnMount: 'always',
    staleTime: 0,
  });
};

export const useSharedTodoLists = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['todolists', 'shared', user?.email],
    queryFn: () => todolistsService.getSharedWithMe(user!.email!),
    enabled: !!user?.email,
    refetchOnMount: 'always',
    staleTime: 0,
  });
};

export const usePendingTodoListInvitations = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['todolists', 'pending', user?.email],
    queryFn: () => todolistsService.getPendingInvitations(user!.email!),
    enabled: !!user?.email,
    refetchOnMount: 'always',
    staleTime: 0,
  });
};

export const useCreateTodoList = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateTodoListInput) =>
      todolistsService.create(input, user!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todolists'] });
    },
  });
};

export const useDeleteTodoList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => todolistsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todolists'] });
    },
  });
};

export const useRespondToTodoListInvitation = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) =>
      todolistsService.respondToInvitation(id, user!.email!, accept, user!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todolists'] });
    },
  });
};
